import React from 'react';
import { AlertTriangle, Check, CloudOff, CloudUpload } from 'lucide-react';
import type { SaveStatus } from '../../hooks/useOfflineSync';

/**
 * Anzeigestatus: SaveStatus plus 'read-only'. Der Lesemodus ist kein Zustand
 * der Offline-Queue (dort wird nie etwas eingereiht), aber der Nutzer braucht
 * an derselben Stelle ein dauerhaftes Signal, dass nichts gespeichert wird.
 */
type ViewSaveStatus = SaveStatus | 'read-only';

const ANZEIGE: Partial<Record<ViewSaveStatus, { text: string; kurz?: string; cls: string; icon: React.ReactNode }>> = {
    // Dauersignal statt Stille: im Lesemodus sucht der Nutzer genau hier
    // nach "Gespeichert" — und soll stattdessen sehen, dass nichts ankommt.
    'read-only': { text: 'Nicht gespeichert (Nur-Ansicht)', kurz: 'Nur-Ansicht', cls: 'bg-amber-100 text-amber-800', icon: <CloudOff className="w-3 h-3" /> },
    'saving': { text: 'Speichert…', cls: 'bg-blue-50 text-blue-600', icon: <CloudUpload className="w-3 h-3" /> },
    'saved': { text: 'Gespeichert', cls: 'bg-emerald-50 text-emerald-600', icon: <Check className="w-3 h-3" /> },
    'queued': { text: 'Wartet auf Sync', cls: 'bg-amber-50 text-amber-600', icon: <CloudUpload className="w-3 h-3" /> },
    'offline': { text: 'Offline – lokal gesichert', kurz: 'Lokal gesichert', cls: 'bg-amber-50 text-amber-700', icon: <CloudOff className="w-3 h-3" /> },
    // 'error' meldet die Queue, wenn ein Stand nicht mehr hinausgeht (zu alt) —
    // „Wartet auf Sync" wäre dort gelogen, Stille auch.
    'error': { text: 'Speicherfehler', cls: 'bg-red-50 text-red-600', icon: <AlertTriangle className="w-3 h-3" /> },
    'storage-error': { text: 'Speicher voll!', cls: 'bg-red-50 text-red-600', icon: <AlertTriangle className="w-3 h-3" /> },
    'auth-required': { text: 'Anmeldung nötig', cls: 'bg-red-50 text-red-600', icon: <AlertTriangle className="w-3 h-3" /> },
};

/**
 * Speicherstand als kleine Plakette; 'idle' und fehlender Status zeigen nichts.
 * `kurz`: immer einzeilig, lange Texte gekürzt (der volle steht im title) —
 * für Leisten mit fester Höhe, in denen eine umbrechende Pille springen würde.
 */
export const SyncBadge: React.FC<{ status?: SaveStatus | 'read-only'; kurz?: boolean }> = ({ status, kurz = false }) => {
    if (!status || status === 'idle') return null;
    const cfg = ANZEIGE[status];
    if (!cfg) return null;
    if (kurz) {
        return <span title={cfg.text} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold whitespace-nowrap ${cfg.cls}`}>{cfg.icon}{cfg.kurz ?? cfg.text}</span>;
    }
    return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold ${cfg.cls}`}>{cfg.icon}{cfg.text}</span>;
};
